/**
 * Where to go after the email magic-link login finishes (public share links with audience
 * `workspace`, template links). Kept in localStorage because the link may open in a new tab;
 * valid for 30 minutes and only for in-app hash paths.
 */
const KEY = 'xc.returnTo';
const TTL_MS = 30 * 60 * 1000;

/** Set while signing out: the page left behind must not become the next person's landing page. */
let suppressedAt = 0;

/**
 * Forget any remembered page and ignore new ones for a few seconds (sign-out re-renders the
 * current page as a login gate before navigating home; that is not a request to return there).
 */
export function suppressReturnTo(): void {
  suppressedAt = Date.now();
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* private mode */
  }
}

export function rememberReturnTo(path: string): void {
  if (!path.startsWith('/')) return;
  if (Date.now() - suppressedAt < 5000) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    /* private mode */
  }
}

export function consumeReturnTo(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof v.path !== 'string' || !v.path.startsWith('/') || v.path.startsWith('//')) return null;
    if (typeof v.at !== 'number' || Date.now() - v.at > TTL_MS) return null;
    return v.path;
  } catch {
    return null;
  }
}
